import type { Collection } from 'pkg/api/models/collection';

import type { CollectionSortOrder } from './sorting';
import type { CollectionOptions } from './use_collection';
import { buildEndpointURL } from './utils';

export interface CollectionState<T> {
	/**
	 * The current URL the collection is fetching data from.
	 */
	url: string;
	/**
	 * Whether the collection is currently loading data.
	 */
	isLoading: boolean;
	/**
	 * Whether the collection has encountered an error when fetching data.
	 */
	hasError?: boolean;
	/**
	 * The records fetched from the collection. If the collection is paginated, this is the current page.
	 * If options.showAllRecords is enabled this is all records fetched so far.
	 */
	records: T[];
	/**
	 * Records that were appended to the collection, outside of the API calls.
	 */
	appendedRecords: T[];
	/**
	 * Records that were prepended to the collection, outside of the API calls.
	 */
	prependedRecords: T[];
	/**
	 * The collection object, containing metadata about the collection.
	 */
	collection?: Collection<T>;
	/**
	 * The records that are currently selected. This can have more records than records.
	 */
	selectedRecords: T[];
	/**
	 * Latest response from the API. Reasonable to check this if hasError is true.
	 */
	response?: Response;
	/**
	 * Current page of the collection.
	 */
	page: number;
}

export function createInitialState<T>(
	endpoint: string,
	options: CollectionOptions<T> = {},
	sortBy?: string,
	sortByOrder?: CollectionSortOrder
): CollectionState<T> {
	let initialPage: number = 1;

	if (options.stateInURL) {
		const qs = new URLSearchParams(window.location.search);

		if (qs.has('page')) {
			initialPage = Number.parseInt(qs.get('page'), 10);
		}
	}

	if (endpoint) {
		const preExistingURLParams = new URLSearchParams(
			endpoint.split('?')[1] || ''
		);

		const optionQueryParams = new URLSearchParams(preExistingURLParams);
		if (options.queryParams) {
			options.queryParams = new URLSearchParams(options.queryParams);
			optionQueryParams.forEach((value, key) => {
				options.queryParams.set(key, value);
			});
		} else {
			options.queryParams = optionQueryParams;
		}

		if (!options.stateInURL) {
			if (preExistingURLParams.has('page')) {
				initialPage = Number.parseInt(preExistingURLParams.get('page'), 10);
			}
		}

		endpoint = endpoint.split('?')[0];
	}

	const initialState: CollectionState<T> = {
		url: '',
		isLoading: true,
		hasError: false,
		records: [],
		appendedRecords: [],
		prependedRecords: [],
		collection: null,
		response: null,
		selectedRecords: options.defaultSelectedRecords || [],
		page: initialPage,
	};

	const endpointUrl = buildEndpointURL(
		endpoint,
		options,
		initialState,
		sortBy,
		sortByOrder
	);
	initialState.url = endpointUrl;
	return initialState;
}
